import React, { forwardRef, useImperativeHandle, useState } from "react";
import { StyleSheet } from "react-native";
import { View, Text } from "../Themed";
import Colors from "../../constants/Colors";
import Icon from "../Icon";
import Pressable from "../Pressable";

export type MoreOptionsModalOption = {
  id: string;
  icon: string;
  text: string;
  onPress: () => void;
  disabled?: boolean;
  redText: boolean;
};

export type MoreOptionsModalRef = {
  show: (options: MoreOptionsModalOption[]) => void;
  hide: () => void;
};

type Props = {
  top?: number;
  right?: number;
};

const MoreOptionsModal = forwardRef<MoreOptionsModalRef, Props>(
  ({ top = 95, right = 20 }, ref) => {
    const [visible, setVisible] = useState(false);
    const [options, setOptions] = useState<MoreOptionsModalOption[]>([]);

    useImperativeHandle(ref, () => ({
      show: (newOptions) => {
        setOptions(newOptions);
        setVisible(true);
      },
      hide: () => {
        setVisible(false);
      },
    }));

    if (!visible) return null;

    return (
      <>
        <Pressable style={styles.overlay} onPress={() => setVisible(false)} />
        <View
          style={[
            styles.modalContainer,
            {
              top,
              right,
            },
          ]}
        >
          {options.map((option) => (
            <Pressable
              key={option.id}
              style={[
                styles.modalItem,
                option.disabled && styles.modalItemDisabled,
              ]}
              onPress={() => {
                if (!option.disabled) {
                  option.onPress();
                  setVisible(false);
                }
              }}
              disabled={option.disabled}
            >
              <Icon
                name={option.icon}
                size={16}
                color={
                  option.disabled
                    ? Colors.nuetral_grey_400
                    : option.redText
                    ? Colors.go_danger_500
                    : Colors.nuetral_grey_700
                }
              />
              <Text
                size={16}
                weight="Regular"
                style={styles.modalItemText}
                lightColor={
                  option.disabled
                    ? Colors.nuetral_grey_400
                    : option.redText
                    ? Colors.go_danger_500
                    : Colors.nuetral_grey_700
                }
              >
                {option.text}
              </Text>
            </Pressable>
          ))}
        </View>
      </>
    );
  }
);

export default MoreOptionsModal;

const styles = StyleSheet.create({
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 1,
  },
  modalContainer: {
    position: "absolute",
    backgroundColor: Colors.base_colors_white,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.nuetral_grey_200,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    zIndex: 2,
  },
  modalItem: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: 16,
    width: "100%",
  },
  modalItemDisabled: {
    opacity: 0.75,
  },
  modalItemText: {
    marginLeft: 10,
    fontFamily: "NunitoRegular",
  },
});
